// mobile-profile — lógica PURA da tela de Perfil/Ajustes mobile (parede A7).
//
// Separada do componente (mesmo padrão de mobile-shell-view.ts / mobile-source.ts)
// pra ser testável sem DOM: o MobileProfile só liga estado de UI nestas funções.
// Aqui moram: a lista de locales e os rótulos de fonte ativa.
//
// A chave de conta (`${providerCode}:${username}`) NÃO mora mais aqui: até 10/10/2026
// o `accountKeyFromPreference` a derivava da preferência só pra esta tela (e devolvia
// null na fonte MANUAL), enquanto o store seguia sem contexto e o sync não rodava no
// celular. Agora o store é hidratado pela fonte da conta (account-context.ts) e a tela
// usa o `accountKey()` do store, como a TV e o sync.

import type { Locale } from '@app-iptv/i18n'

/** Os 3 locales do MVP (mesma lista da SettingsScreen TV). Os endônimos ficam
 *  iguais nos 3 idiomas por design (allowlist do gate de i18n). */
export const PROFILE_LOCALES: ReadonlyArray<{ value: Locale; label: string }> = [
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'es-419', label: 'Español (LATAM)' },
]

/** Preferência (fonte) ativa lida do backend, sem senha. */
export interface ActiveSourcePreference {
  /**
   * `null` quando a fonte é MANUAL: o `GET /preference` omite o code efêmero
   * (`D{hash}`) de propósito, por ser identificador interno. Era `string` e o
   * typecheck pegou a divergência quando o contrato mudou.
   */
  providerCode: string | null
  xtreamUsername: string
  serverUrl?: string | null
  /**
   * Nome da lista — só no modo manual (ver `sourceLabel`).
   *
   * ⚠️ HOJE ELE NUNCA VEM: o `GET /v1/swipe-account/preference` responde
   * `{sourceType, providerCode, xtreamUsername, serverUrl, updatedAt}` e nada mais
   * (medido em be0f6e42, develop e main). Fica declarado porque o cliente já sabe
   * usá-lo quando o contrato incluir — até lá, `sourceLabel` cai no host.
   */
  label?: string | null
  sourceType?: 'code' | 'manual'
}

/**
 * Rótulo curto da fonte ativa pra exibir no card "Fonte".
 *
 * Modo CÓDIGO: `CÓDIGO · usuário` (como sempre foi).
 * Modo MANUAL: `Nome da lista · usuário` quando o servidor manda o nome; como
 * hoje ele NÃO manda (ver `label`), cai no HOST da `serverUrl` —
 * `servidor.com:8080 · usuário`. Sem esse degrau o card mostrava "sem fonte" +
 * CTA "configurar" pra quem TEM uma lista pessoal ativa: a tela negava a
 * existência da fonte, que é pior que mostrar um nome técnico. O code efêmero
 * (`D3F9K2Q`) continua fora — é interno e não diz nada ao usuário.
 *
 * Sem nenhum dos três → null (caller mostra "sem fonte" + CTA configurar).
 */
export function sourceLabel(pref: ActiveSourcePreference | null | undefined): string | null {
  if (!pref) return null
  const user = pref.xtreamUsername?.trim()
  const head = pref.label?.trim() || pref.providerCode?.trim() || hostDaFonte(pref)
  if (!head) return null
  return user ? `${head} · ${user}` : head
}

/** Host (com porta) da URL da fonte manual. Só entra como ÚLTIMO recurso, e só
 *  quando a fonte é manual — no modo código a URL é metadado opcional e o nome
 *  certo é o código. URL inválida/ausente → null (o caller já trata). */
function hostDaFonte(pref: ActiveSourcePreference): string | null {
  if (pref.sourceType !== 'manual') return null
  const url = pref.serverUrl?.trim()
  if (!url) return null
  try {
    return new URL(url).host || null
  } catch {
    return null
  }
}

/** Normaliza um valor de locale arbitrário pro Locale suportado mais próximo
 *  (default pt-BR). Defensivo contra i18n.language vir com sufixo/região. */
export function normalizeLocale(value: string | undefined | null): Locale {
  const v = (value ?? '').toLowerCase()
  if (v.startsWith('en')) return 'en-US'
  if (v.startsWith('es')) return 'es-419'
  return 'pt-BR'
}

/** Limite máximo de caracteres do nome de perfil (igual ao slice do createProfile
 *  no local-db e ao maxLength dos inputs). */
export const PROFILE_NAME_MAX = 30

/**
 * Valida e normaliza o nome de perfil pra criar/editar. Retorna `{ name }` com o
 * nome já trimado/cortado quando válido, ou `{ error: 'profiles.nameRequired' }`
 * quando vazio. Mesma regra do createProfile (trim + slice 30) — manter aqui
 * deixa a validação testável sem DOM e idêntica entre criar e editar.
 */
export function validateProfileName(
  raw: string,
): { name: string } | { error: 'profiles.nameRequired' } {
  const name = raw.trim().slice(0, PROFILE_NAME_MAX)
  if (name.length < 1) return { error: 'profiles.nameRequired' }
  return { name }
}

/**
 * Regra "não deixar a conta sem nenhum perfil": só permite excluir quando há mais
 * de um perfil. O último perfil é bloqueado (a conta sempre tem ao menos 1). Igual
 * em espírito ao que a ProfilesScreen TV faz (ao zerar volta pro modo criação);
 * no mobile bloqueamos antes pra não cair em estado sem perfil ativo.
 */
export function canDeleteProfile(profileCount: number): boolean {
  return profileCount > 1
}

/**
 * Decide qual perfil deve virar o ativo DEPOIS de excluir `deletedId`.
 * - Se o perfil excluído NÃO é o ativo → null (não precisa trocar).
 * - Se o perfil excluído É o ativo → o primeiro perfil restante (o app nunca
 *   fica com `activeProfileId` apontando pra um perfil que não existe mais).
 * - Sem perfil restante → null (caso teórico; a UI bloqueia excluir o último).
 */
export function nextActiveAfterDelete<T extends { id: string }>(
  profiles: ReadonlyArray<T>,
  deletedId: string,
  activeId: string | null,
): T | null {
  if (deletedId !== activeId) return null
  const remaining = profiles.filter((p) => p.id !== deletedId)
  return remaining[0] ?? null
}
